import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ChevronRight, SlidersHorizontal, Loader2, Car, X } from "lucide-react";
import api from "../services/api";
import ProductCard from "../components/ProductCard";
import { useAuth } from "../context/AuthContext";

const CategoryPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const query = searchParams.get("q") || "";
  const plate = searchParams.get("plate") || "";
  const [myVehicles, setMyVehicles] = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const isSearch = slug === undefined;
  const isPromo = slug === "promotions";

  const [sort, setSort] = useState("pop");
  const [selBrands, setSelBrands] = useState([]);
  const [maxPrice, setMaxPrice] = useState(300);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catName, setCatName] = useState("");

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      const found = data.find((c) => c.slug === slug);
      setCatName(found ? found.name : slug);
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!user) { setMyVehicles([]); return; }
    api.get("/me/vehicles").then(({ data }) => setMyVehicles(data)).catch(() => setMyVehicles([]));
  }, [user]);

  const setPlate = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p) next.set("plate", p); else next.delete("plate");
    setSearchParams(next);
  };

  useEffect(() => {
    setLoading(true);
    setVehicleInfo(null);
    const sortFn = (list) => {
      const out = [...list];
      if (sort === "asc") out.sort((a, b) => a.price - b.price);
      if (sort === "desc") out.sort((a, b) => b.price - a.price);
      if (sort === "rating") out.sort((a, b) => b.rating - a.rating);
      return maxPrice < 300 ? out.filter((p) => p.price <= maxPrice) : out;
    };
    if (plate && !isPromo && !isSearch) {
      api.get(`/vehicle/${encodeURIComponent(plate)}/parts`, { params: { category: slug } })
        .then(({ data }) => { setVehicleInfo(data.vehicle); setProducts(sortFn(data.products || [])); })
        .catch(() => { setVehicleInfo({ error: true, plate }); setProducts([]); })
        .finally(() => setLoading(false));
      return;
    }
    const params = {};
    if (!isSearch && !isPromo) params.category = slug;
    if (isSearch && query) params.q = query;
    if (isPromo) params.promo = true;
    if (sort !== "pop") params.sort = sort;
    if (maxPrice < 300) params.max_price = maxPrice;
    api.get("/products", { params })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [slug, query, isSearch, isPromo, sort, maxPrice, plate]);

  const title = isSearch
    ? (query ? `Résultats pour « ${query} »` : "Tous les produits")
    : isPromo ? "Promotions" : (catName || slug);

  const availableBrands = [...new Set(products.map((p) => p.brand))];
  const filtered = selBrands.length ? products.filter((p) => selBrands.includes(p.brand)) : products;

  const toggleBrand = (b) =>
    setSelBrands((prev) => (prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]));

  return (
    <div className="max-w-[1440px] mx-auto px-5 py-8">
      <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-5">
        <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
        <ChevronRight size={15} />
        <span className="text-slate-50 font-semibold">{title}</span>
      </div>

      <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-4xl mb-6">{title}</h1>

      {!isSearch && !isPromo && (user ? (
        <div data-testid="vehicle-filter" className={`flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 mb-6 ${plate ? "border-[#E01F1F]/60 bg-[#E01F1F]/5" : "border-[#1E232D] bg-[#10131A]"}`}>
          <Car size={18} className="text-[#FF3B3B]" />
          <span className="text-[14px] font-semibold text-slate-50">Compatible avec mon véhicule</span>
          {myVehicles.length === 0 ? (
            <Link to="/compte?section=garage" className="text-[13px] text-[#FF3B3B] font-semibold hover:underline">Ajouter un véhicule dans Mon garage</Link>
          ) : (
            <select data-testid="vehicle-filter-select" value={plate} onChange={(e) => setPlate(e.target.value)} className="border-2 border-[#222735] bg-[#08090C] rounded-lg px-3 py-1.5 text-[13px] text-slate-50 outline-none focus:border-[#E01F1F]">
              <option value="">Tous les véhicules</option>
              {myVehicles.map((v) => <option key={v.id} value={v.plate}>{v.plate} · {v.nickname || v.label || "véhicule"}</option>)}
            </select>
          )}
          {plate && (
            <span className="text-[13px] text-slate-300 flex items-center gap-2" data-testid="vehicle-filter-active">
              {vehicleInfo?.error ? <span className="text-red-400">Véhicule {plate} introuvable</span> : vehicleInfo ? <>Filtré pour <strong className="text-slate-50">{vehicleInfo.label || plate}</strong></> : "Identification…"}
              <button data-testid="vehicle-filter-clear" onClick={() => setPlate("")} className="p-1 rounded hover:bg-[#222735] text-slate-400 hover:text-white"><X size={14} /></button>
            </span>
          )}
        </div>
      ) : plate ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#1E232D] bg-[#10131A] px-4 py-3 mb-6 text-[14px] text-slate-300"><Car size={18} className="text-[#FF3B3B]" /> Filtre véhicule <strong className="text-slate-50">{plate}</strong>{vehicleInfo?.label && <> · {vehicleInfo.label}</>}<button onClick={() => setPlate("")} className="p-1 rounded hover:bg-[#222735] text-slate-400 hover:text-white"><X size={14} /></button></div>
      ) : null)}

      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-64 shrink-0">
          <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-5">
            <div className="flex items-center gap-2 font-bold text-slate-50 mb-4">
              <SlidersHorizontal size={18} className="text-[#FF3B3B]" /> Filtres
            </div>
            <div className="mb-6">
              <div className="font-semibold text-slate-50 text-[14px] mb-3">Prix maximum</div>
              <input type="range" min={10} max={300} value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full accent-[#E01F1F]" />
              <div className="text-[14px] text-slate-400 mt-1">Jusqu'à <strong className="text-slate-50">{maxPrice} €</strong></div>
            </div>
            <div>
              <div className="font-semibold text-slate-50 text-[14px] mb-3">Marques</div>
              <div className="space-y-2">
                {availableBrands.map((b) => (
                  <label key={b} className="flex items-center gap-2 text-[14px] text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={selBrands.includes(b)} onChange={() => toggleBrand(b)} className="w-4 h-4 accent-[#E01F1F]" />
                    {b}
                  </label>
                ))}
                {availableBrands.length === 0 && <span className="text-[13px] text-slate-500">—</span>}
              </div>
            </div>
          </div>
        </aside>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-5">
            <span className="text-[14px] text-slate-400">{filtered.length} produit(s)</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border-2 border-[#222735] rounded-lg px-3 py-2 text-[14px] text-slate-50 font-medium outline-none focus:border-[#E01F1F]">
              <option value="pop">Popularité</option>
              <option value="asc">Prix croissant</option>
              <option value="desc">Prix décroissant</option>
              <option value="rating">Mieux notés</option>
            </select>
          </div>
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FF3B3B]" size={36} /></div>
          ) : filtered.length === 0 ? (
            <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-16 text-center text-slate-400">Aucun produit ne correspond à votre recherche.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {filtered.map((p) => (<ProductCard key={p.id} product={p} />))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
